import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SynchronizationCandidates, Trademark, TrademarkDTO, TrademarkDTOSearch, TrademarkStatus } from '../models/trademark';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Paginator } from '../models/paginator';
import { ProductService } from '../models/productService';


export interface ExternalCreateTrademarkResult {
  ok: boolean,
  error?: string,
  trademark: Trademark
}


@Injectable({
  providedIn: 'root'
})
export class TrademarkService {


  private trademarks: Trademark[] = [];

  constructor(
    private http: HttpClient
  ) {

  }

  countAll(countriesOfDesignation?: string[], classNumbers?: number[], query?: string): Promise<{ count: number }> {
    let params = new HttpParams();

    if (countriesOfDesignation) {
      countriesOfDesignation.forEach(country => {
        params = params.append('countryOfDesignation[]', country.toUpperCase());
      })
    }

    if (classNumbers) {
      classNumbers.forEach(classNumber => {
        params = params.append('classNumber[]', classNumber);
      })
    }

    if (query) {
      params = params.set('q', query);
    }

    return firstValueFrom(this.http
      .get<{ count: number }>(`${environment.backend}/trademarks/count`, { params }));
  }

  graphAll() {
    return firstValueFrom(this.http
      .get<{ obligated: number, notObligated: number }>(`${environment.backend}/trademarks/graph`));
  }

  latests(): Promise<Trademark[]> {
    return firstValueFrom(this.http
      .get<Trademark[]>(`${environment.backend}/trademarks/latests`));
  }

  geographies(): Promise<{ _id: string, count: number }[]> {
    return firstValueFrom(this.http
      .get<{ _id: string, count: number }[]>(`${environment.backend}/trademarks/geographies`));
  }


  retrieveAll(pageNumber: number, limit: number, offset: number, sort?: string, countriesOfDesignation?: string[], classNumbers?: number[], query?: string, families?: string[], synchronization?: string[]): Promise<Paginator<Trademark>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }

    if (countriesOfDesignation) {
      countriesOfDesignation.forEach(country => {
        params = params.append('countryOfDesignation[]', country.toUpperCase());
      })
    }

    if (classNumbers) {
      classNumbers.forEach(classNumber => {
        params = params.append('classNumber[]', classNumber);
      })
    }

    if (query) {
      params = params.set('q', query);
    }

    if (families) {
      families.forEach(family => {
        params = params.append('family[]', family);
      })
    }

    if (synchronization) {
      synchronization.forEach(value => {
        params = params.append('synchronization[]', value);
      })
    }

    return firstValueFrom(this.http
      .get<Paginator<Trademark>>(`${environment.backend}/trademarks/search`, { params }));
  }

  create(trademark: Trademark): Promise<Trademark> {
    return firstValueFrom(this.http
      .post<Trademark>(`${environment.backend}/trademarks`, trademark));
  }

  createExternal(trademarks: TrademarkDTO[]): Promise<{ declared: ExternalCreateTrademarkResult[], errors: ExternalCreateTrademarkResult[] }> {
    return firstValueFrom(this.http
      .post<{ declared: ExternalCreateTrademarkResult[], errors: ExternalCreateTrademarkResult[] }>(`${environment.backend}/trademarks/external`, trademarks));
  }

  retrieve(id: string): Promise<Trademark> {
    return firstValueFrom(this.http
      .get<Trademark>(`${environment.backend}/trademarks/${id}`));
  }

  retrieveByIds(ids: string[]): Promise<Trademark[]> {
    const params = new HttpParams().appendAll({ ids });

    return firstValueFrom(
      this.http.get<Trademark[]>(
        `${environment.backend}/trademarks`,
        { params },
      )
    );
  }

  retrieveProductsAndServices(id: string): Promise<ProductService[]> {
    return firstValueFrom(this.http
      .get<ProductService[]>(`${environment.backend}/trademarks/${id}/productsAndServices`));
  }

  retrieveExternal(id: string): Promise<Trademark> {
    return firstValueFrom(this.http
      .get<Trademark>(`${environment.backend}/trademarks/${id}/external`));
  }

  retrieveObligationOfUse(id: string): Promise<{ isObligated: boolean, key: string, value: number, unit: string }> {
    return firstValueFrom(this.http
      .get<{ isObligated: boolean, key: string, value: number, unit: string }>(`${environment.backend}/trademarks/${id}/obligation`));
  }

  // The office answers found as the bulk synchronisation finds them, each with what it has in common with the trademark
  synchronizationCandidates(id: string): Promise<SynchronizationCandidates> {
    return firstValueFrom(this.http
      .post<SynchronizationCandidates>(`${environment.backend}/trademarks/${id}/synchronization/candidates`, null));
  }

  // synchronized = the update applies data read from the office registers
  update(trademark: Trademark, synchronized: boolean = false): Promise<Trademark> {
    delete trademark.organizationId;
    let body: any = { ...trademark };
    // The synchronization state is maintained by the backend only
    delete body.synchronization;
    Object.keys(body).forEach(key => {
      if (!body[key]) body[key] = null;
    });
    let params = new HttpParams();
    if (synchronized) {
      params = params.set('synchronized', true);
    }
    return firstValueFrom(this.http
      .put<Trademark>(`${environment.backend}/trademarks/${trademark._id}`, body, { params }));
  }

  delete(trademark: Trademark): Promise<any> {
    return firstValueFrom(this.http
      .delete<Trademark>(`${environment.backend}/trademarks/${trademark._id}`));
  }

  updateFamily(trademark: Trademark, family?: string): Promise<Trademark> {
    return firstValueFrom(this.http
      .put<Trademark>(`${environment.backend}/trademarks/${trademark._id}/family`, { family }));
  }

  searchOne(identifierNumber: string, countriesOfDesignation: string[]) {
    let params = new HttpParams()
      .set('identifierNumber', identifierNumber)

    if (countriesOfDesignation) {
      countriesOfDesignation.forEach(country => {
        params = params.append('countryOfDesignation[]', country);
      })
    }
    return firstValueFrom(this.http
      .get<Trademark>(`${environment.backend}/trademarks/search/external`, { params }));
  }

  retrieveAllForDocument(documentId: string): Promise<Trademark[]> {
    return firstValueFrom(this.http
      .get<Trademark[]>(`${environment.backend}/trademarks/documents/${documentId}`));
  }

  search(trademarks: { identifierNumber: string, countryOfDesignation: string[] }[]): Promise<TrademarkDTOSearch[]> {
    return firstValueFrom(this.http
      .post<TrademarkDTOSearch[]>(`${environment.backend}/trademarks/search/external`, { trademarks }));
  }

  uploadReproduction(reproduction: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("image", reproduction);
    return firstValueFrom(this.http.post<{ url: string }>(`${environment.backend}/upload/trademarkReproduction`, formData));
  }


  _statusMapper(status: string): { status: TrademarkStatus, color: string, colorHex: string } {
    if (status && status != '') {
      if (status.toLowerCase().includes("unfiled")) {
        return {
          status: TrademarkStatus.UNFILED,
          color: 'light',
          colorHex: '#747479'
        }
      } else if (status.toLowerCase().includes("filed")) {
        return {
          status: TrademarkStatus.FILED,
          color: 'orange',
          colorHex: '#e38847'
        }
      } else if (status.toLowerCase().includes("opposed")) {
        return {
          status: TrademarkStatus.OPPOSED,
          color: 'red',
          colorHex: '#e34754'
        }
      } else if (status.toLowerCase().includes("registered")) {
        return {
          status: TrademarkStatus.REGISTERED,
          color: 'green',
          colorHex: '#3a9e31'
        }
      } else if (status.toLowerCase().includes("published")) {
        return {
          status: TrademarkStatus.PUBLISHED,
          color: 'orange',
          colorHex: '#e38847'
        }
      } else if (status.toLowerCase().includes("expired")) {
        return {
          status: TrademarkStatus.EXPIRED,
          color: 'red',
          colorHex: '#e34754'
        }
      } else if (status.toLowerCase().includes("ended")) {
        return {
          status: TrademarkStatus.ENDED,
          color: 'red',
          colorHex: '#e34754'
        }
      } else if (status.toLowerCase().includes("pending")) {
        return {
          status: TrademarkStatus.PENDING,
          color: 'orange',
          colorHex: '#e38847'
        }
      } else if (status.toLowerCase().includes("common") && status.toLowerCase().includes("law")) {
        return {
          status: TrademarkStatus.COMMON_LAW,
          color: 'blue',
          colorHex: '#47a2e3'
        }
      } else {
        return {
          status: TrademarkStatus.UNKNOWN,
          color: 'light',
          colorHex: '#747479'
        }
      }
    } else {
      return {
        status: TrademarkStatus.UNKNOWN,
        color: 'light',
        colorHex: '#747479'
      }
    }

  }

}
